// Song catalog (the browsable directory). `catalog.json` is generated from the
// per-song entry.json tree by scripts/build-catalog.mjs. Included entries are
// served same-origin (precached by the deploy); downloadable entries carry an
// absolute CORS URL, fetched on first pick and cached on device (song-cache.ts).

import { getCached, putCached } from "../store/song-cache";
import type { CurriculumTags } from "../types";
import type { Features } from "./difficulty-features";

/** One entry in songs/catalog.json. Optionally carries curriculum tags. */
export interface CatalogEntry extends CurriculumTags {
  id: string;
  title: string;
  composer: string;
  difficulty: string;
  source: string;
  sourceUrl: string;
  license: string;
  maintainer: string;
  /** Path to the .mid relative to the served root, or an absolute https:// URL. */
  file: string;
  /** Pre-computed audio features (written by build-catalog). Absent for link entries. */
  features?: Features;
}

/** Load the manifest. Returns [] if the catalog is absent (e.g. not fetched). */
export async function loadCatalog(): Promise<CatalogEntry[]> {
  try {
    const res = await fetch("songs/catalog.json");
    if (!res.ok) return [];
    return (await res.json()) as CatalogEntry[];
  } catch {
    return [];
  }
}

/** Fetch the raw MIDI bytes for a catalog entry. */
async function fetchSongBytes(entry: CatalogEntry): Promise<ArrayBuffer> {
  const res = await fetch(entry.file);
  if (!res.ok) throw new Error(`Failed to fetch ${entry.file} (${res.status})`);
  return res.arrayBuffer();
}

/** A downloadable entry's `file` is an absolute URL; included entries are relative. */
export function isDownloadable(entry: CatalogEntry): boolean {
  return /^https?:\/\//.test(entry.file);
}

/** Injectable byte-source seam (keeps getSongBytes testable without IndexedDB). */
export interface SongBytesDeps {
  getCached(id: string): Promise<ArrayBuffer | undefined>;
  putCached(id: string, bytes: ArrayBuffer): Promise<void>;
  fetchBytes(entry: CatalogEntry): Promise<ArrayBuffer>;
}

const DEFAULT_DEPS: SongBytesDeps = { getCached, putCached, fetchBytes: fetchSongBytes };

/**
 * Resolve a song's bytes: cache hit → return; miss → fetch, and write-through
 * cache only downloadable entries (included songs are precached by the deploy).
 */
export async function getSongBytes(
  entry: CatalogEntry,
  deps: SongBytesDeps = DEFAULT_DEPS,
): Promise<ArrayBuffer> {
  const hit = await deps.getCached(entry.id);
  if (hit) return hit;
  const bytes = await deps.fetchBytes(entry);
  if (isDownloadable(entry)) await deps.putCached(entry.id, bytes);
  return bytes;
}

/** Dropdown label: ↓ prefix for an uncached downloadable song; plain otherwise. */
export function catalogOptionLabel(entry: CatalogEntry, isCached: boolean): string {
  const base = `${shortComposer(entry.composer)} — ${entry.title}`;
  return isDownloadable(entry) && !isCached ? `↓ ${base}` : base;
}

/** Short attribution line shown in the status bar when a bundled song loads. */
export function attributionLine(entry: CatalogEntry): string {
  return `${entry.title} · ${shortComposer(entry.composer)} · ${entry.source} (${entry.license})`;
}

/** Drop a trailing "(1810-1856)" style lifespan from a composer name. Coerces
 *  defensively so a malformed catalog.json entry can't throw. */
function shortComposer(composer: string): string {
  return String(composer ?? "")
    .replace(/\s*\([^)]*\)\s*$/, "")
    .trim();
}
